import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import {
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import { hasCategory } from "@/components/game-categories/helpers";
import {
  DislikeIcon,
  LikeIcon,
  LoveIcon,
} from "@/components/icons/big-rating-icons";
import ProfileCard from "@/components/profile-card";

import OptionsMenu from "../options-menu";
import type { GameType, ReviewType } from "../types";

interface DetailsProps {
  game: GameType;
  isFullReview: boolean;
  review: ReviewType;
}

export default function Details({ isFullReview, game, review }: DetailsProps) {
  const user = useMemo(() => review.user, [review.user]);

  const rating = useMemo(() => {
    if (review.overallRating === "DISLIKE") {
      return <DislikeIcon />;
    }
    if (review.overallRating === "LIKE") {
      return <LikeIcon />;
    }
    if (review.overallRating === "LOVE") {
      return <LoveIcon />;
    }
    return null;
  }, [review.overallRating]);

  const counterText = useMemo(() => {
    if (!game.primaryCategory) {
      return null;
    }
    if (!user.experiencesCountFacets) {
      return null;
    }

    let category = "";
    if (hasCategory(game.primaryCategory, CATEGORY_ESCAPE_ROOM)) {
      category = CATEGORY_ESCAPE_ROOM;
    }
    if (hasCategory(game.primaryCategory, CATEGORY_HAUNT)) {
      category = CATEGORY_HAUNT;
    }

    if (!category) {
      return null;
    }

    const counter =
      user.experiencesCountFacets.find(
        (facet) => facet.category.slug === category
      )?.count || 0;

    if (category === CATEGORY_HAUNT) {
      return `${counter} haunt${counter === 1 ? "" : "s"}`;
    }

    return `${counter} escape room${counter === 1 ? "" : "s"}`;
  }, [game.primaryCategory, user.experiencesCountFacets]);

  return (
    <div className="flex justify-between">
      <div className="relative z-1 flex items-center">
        <CloudinaryImage
          className="size-10 rounded-full"
          rW={80}
          size={40}
          user={user}
        />
        <div className="ml-2.5 flex flex-col items-start gap-0.5 md:gap-[3px]">
          <h5 className="font-semibold text-15/[1.2em]">
            <ProfileCard userId={user.id}>
              <Link
                className="link-overlay hover:underline"
                to={`/@${user.username}`}
              >
                {user.username}
              </Link>
            </ProfileCard>
          </h5>
          {counterText && (
            <p className="text-13/[1.2em] text-cool-gray">{counterText}</p>
          )}
        </div>
      </div>
      <div className="flex items-center">
        {!isFullReview && (
          <OptionsMenu
            className="relative z-1 hidden h-full w-14 md:block"
            game={game}
            review={review}
          />
        )}
        {rating}
      </div>
    </div>
  );
}
